import React, { FC, useState } from 'react';
import { observer } from 'mobx-react-lite';
import { CustomSpinner } from '@yakoffice/custom-spinner';
import { useUsers, useProjects, useTools } from '../../components';
import styles from './users-styles.module.sass';
import { DocumentTitle } from '@yakoffice/shared-components';
import { NavigationRail } from '@yakoffice/navigation-rail';
import Container from 'react-bootstrap/Container';
import { InputGroup } from 'react-bootstrap';
import Form from 'react-bootstrap/Form';
import Col from 'react-bootstrap/Col';
import Button from 'react-bootstrap/Button';
import { IUser } from '../../model/user';
import { useAuthUser } from '@yakoffice/yakoffice-firebase';
import Row from 'react-bootstrap/Row';
import Alert from 'react-bootstrap/Alert';
import { ProjectData, UserOverviewCard } from './cards/userOverviewCard';
import { UserCard } from './cards/userCard';
import { NewUserCard } from './cards/newUserCard';

export const UsersIndex: FC = observer(() => {

    const loadProjectsResult = useProjects();
    const loadToolsResult = useTools();
    const [searchUser, setSearchUser] = useState<string>();
    const [activeUser, setActiveUser] = useState<IUser | null>();
    const [newUser, setNewUser] = useState<boolean>(false);
    const authUser = useAuthUser();
    const isAuthUserIamAdmin = !!authUser && authUser.yoUser?.isAdmin;
    const [callbackTrigger, setCallbackTrigger] = useState<number>(0);
    const loadUsersResult = useUsers(callbackTrigger);

    DocumentTitle('Users - Portal');

    const createNewUser = () => {
        setActiveUser(undefined);
        setNewUser(true);
    };

    const updateCallbackTrigger = (clearActiveUser?: boolean) => {
        setCallbackTrigger(callbackTrigger + 1);
        clearActiveUser && setActiveUser(undefined);
    };

    const updateNewUserCallback = (clearActiveUser?: boolean) => {
        setCallbackTrigger(callbackTrigger + 1);
        clearActiveUser && setActiveUser(undefined);
        setNewUser(false);
    };


    const renderUsers = () => {

        if (!loadUsersResult.isLoaded)
            return <CustomSpinner spinnerText={'Loading project users...'} position="relative" />;

        if (loadUsersResult.isLoaded && loadUsersResult.users?.length === 0)
            return <Alert variant="info">No users found for this project</Alert>;

        const showUsers = searchUser == null ? loadUsersResult.users : loadUsersResult.users?.filter(user => user.email.includes(searchUser.toLowerCase()));

        return showUsers?.map(user => {

            const email = user.email;
            const hasAdminPermissions = user.isAdmin;
            const projectDate: ProjectData[] = [];

            loadProjectsResult.projects?.map(project => {
                projectDate.push({
                    hasClaims: !!user.iamRolesForProject(project.id),
                    claimType: user.iamRolesForProject(project.id),
                    logoSrc: project.imageUrl ?? ''
                });
            });

            return <div onClick={() => {
                setNewUser(false);
                setActiveUser(user);
            }} key={user.id}><UserOverviewCard email={email}
                                               hasAdminPermissions={hasAdminPermissions}
                                               projects={projectDate}
                                               active={activeUser === user} />
            </div>;
        });
    };

    return !loadProjectsResult.isLoaded || !loadToolsResult.isLoaded || !loadUsersResult.isLoaded
        ? <CustomSpinner spinnerText="Loading Users..." />

        :
        <div className={`${styles.project} project`}>
            <NavigationRail toolId="portal" />

            <div className={`${styles.projectContent} projectContent`}>
                <Container fluid className={styles.stickyContainer}>
                    <div className={styles.usersTitle}>
                        <h1 className={styles.title}>Users</h1>
                        <div className={styles.searchForm}>
                            <InputGroup>
                                <Form.Control type="email" placeholder="Search by email address"
                                              className={styles.userSearchField}
                                              onChange={(e) => setSearchUser(e.target.value)}
                                              disabled={!loadUsersResult.isLoaded || loadUsersResult.users?.length === 0}
                                />
                            </InputGroup>
                        </div>
                        <Col xs="auto">
                            <Button className={styles.addUserButton} onClick={createNewUser}
                                    disabled={!isAuthUserIamAdmin}>
                                <i className="fas fa-user-plus" /> New user
                            </Button>
                        </Col>
                    </div>
                </Container>

                <Container fluid>
                    <Row>
                        <Col xl={4} lg={5} md={6} className={styles.scrollable}>
                            {renderUsers()}
                        </Col>
                        <Col xl={8} lg={7} md={6} className={styles.scrollable}>
                            <div className={styles.userCardWrapper}>
                                <fieldset disabled={!isAuthUserIamAdmin}>
                                    {activeUser && <UserCard user={activeUser}
                                                             updateCallbackTrigger={updateCallbackTrigger} />}
                                    {newUser && <NewUserCard updateCallbackTrigger={updateNewUserCallback} />}
                                </fieldset>
                            </div>
                        </Col>
                    </Row>
                </Container>
            </div>
        </div>;
});

export default UsersIndex;

